import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, FileDown, Save, Table2, Copy, History } from "lucide-react";
import GenerateQuoteDialog from "./GenerateQuoteDialog";
import api, { formatError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";

const blankRow = () => ({ product_name: "", specification: "", qty: "", rate: "" });
const n = (v) => { const x = parseFloat(String(v ?? "").replace(/,/g, "")); return Number.isFinite(x) && x > 0 ? x : 0; };
const inr = (v) => `₹ ${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const COLS = ["product_name", "specification", "qty", "rate"];

export function parseClipboardGrid(text) {
  const rows = [];
  let row = [], cur = "", q = false;
  const t = text.replace(/\r\n?/g, "\n");
  for (let i = 0; i < t.length; i++) {
    const c = t[i];
    if (q) {
      if (c === '"' && t[i + 1] === '"') { cur += '"'; i++; }
      else if (c === '"') q = false;
      else cur += c;
    } else if (c === '"' && cur === "") q = true;
    else if (c === "\t") { row.push(cur); cur = ""; }
    else if (c === "\n") { row.push(cur); rows.push(row); row = []; cur = ""; }
    else cur += c;
  }
  if (cur !== "" || row.length) { row.push(cur); rows.push(row); }
  return rows.map((r) => r.map((x) => x.trim())).filter((r) => r.some(Boolean));
}

const istInputValue = (iso) => {
  const d = iso ? new Date(iso) : new Date();
  const p = Object.fromEntries(new Intl.DateTimeFormat("en-GB", { timeZone: "Asia/Kolkata", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(d).map((x) => [x.type, x.value]));
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
};
export const fmtIST = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true }).toUpperCase() : "—");

const numOnly = (v) => String(v || "").replace(/[^0-9.]/g, "");

export function computeWorksheet(rows, gstIncluded, gstRate, shipIncluded, shipAmt) {
  const items = rows.map((r) => ({ ...r, total: Math.round(n(r.qty) * n(r.rate) * 100) / 100 }));
  const subtotal = items.reduce((a, r) => a + r.total, 0);
  const gst = gstIncluded ? 0 : Math.round(subtotal * (Math.min(n(gstRate), 100) / 100) * 100) / 100;
  const shipping = shipIncluded ? 0 : n(shipAmt);
  return { items, subtotal, gst, shipping, grand: subtotal + gst + shipping };
}

function YesNo({ value, onChange, testid }) {
  return (
    <div className="inline-flex rounded-md border border-slate-300 overflow-hidden text-[11px] font-semibold" data-testid={testid}>
      {[["Yes", true], ["No", false]].map(([label, v]) => (
        <button key={label} type="button" onClick={() => onChange(v)} className={`px-3 py-1 transition-colors ${value === v ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-100"}`} data-testid={`${testid}-${label.toLowerCase()}`}>{label}</button>
      ))}
    </div>
  );
}

export default function QuoteWorksheetDialog({ target, onOpenChange, onSaved }) {
  const open = !!target;
  const [rows, setRows] = useState([blankRow()]);
  const [gstIncluded, setGstIncluded] = useState(true);
  const [gstRate, setGstRate] = useState("18");
  const [shipIncluded, setShipIncluded] = useState(true);
  const [shipAmt, setShipAmt] = useState("");
  const [busy, setBusy] = useState(false);
  const [quoteDate, setQuoteDate] = useState(istInputValue());
  const [internalRemark, setInternalRemark] = useState("");
  const [clientGst, setClientGst] = useState("");
  const [showClientGst, setShowClientGst] = useState(false);
  const [siblings, setSiblings] = useState([]);
  const [genOpen, setGenOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    api.get(`/quote-requests/${target.id}/sibling-worksheets`).then(({ data }) => setSiblings(data || [])).catch(() => setSiblings([]));
  }, [open, target?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    const ws = target.worksheet;
    setRows(ws?.items?.length ? ws.items.map((i) => ({ product_name: i.product_name, specification: i.specification, qty: String(i.qty || ""), rate: String(i.rate || "") })) : [blankRow(), blankRow(), blankRow()]);
    setGstIncluded(ws ? !!ws.gst_included : true);
    setGstRate(ws && !ws.gst_included ? String(ws.gst_rate) : "18");
    setShipIncluded(ws ? !!ws.shipping_included : true);
    setShipAmt(ws && !ws.shipping_included ? String(ws.shipping_amount) : "");
    setQuoteDate(istInputValue(ws?.quote_date));
    setInternalRemark(ws?.internal_remark || "");
    setClientGst(ws?.client_gst || "");
    setShowClientGst(!!ws?.show_client_gst);
  }, [open, target?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const calc = useMemo(() => computeWorksheet(rows, gstIncluded, gstRate, shipIncluded, shipAmt), [rows, gstIncluded, gstRate, shipIncluded, shipAmt]);
  if (!open) return null;

  const onPaste = (e, i, col) => {
    const text = e.clipboardData?.getData("text/plain") || "";
    if (!/[\t\n]/.test(text.trim())) return;
    e.preventDefault();
    let grid = parseClipboardGrid(text);
    if (!grid.length) return;
    let startCol = COLS.indexOf(col);
    const width = Math.max(...grid.map((r) => r.length));
    const headerLike = (r) => r.some((x) => /product|specification|qty|rate/i.test(x)) && !r.some((x) => /^\d+(\.\d+)?$/.test(x));
    if (headerLike(grid[0])) grid = grid.slice(1);
    if (!grid.length) return;
    if (startCol === 0 && width >= 5 && grid.every((r) => !r[0] || /^\d+\.?$/.test(r[0]))) grid = grid.map((r) => r.slice(1));
    setRows((prev) => {
      const next = [...prev];
      grid.forEach((cells, r) => {
        const idx = i + r;
        while (next.length <= idx) next.push(blankRow());
        const rowObj = { ...next[idx] };
        cells.slice(0, COLS.length - startCol).forEach((v, c) => {
          const key = COLS[startCol + c];
          rowObj[key] = key === "qty" || key === "rate" ? numOnly(v) : v;
        });
        next[idx] = rowObj;
      });
      return next;
    });
    toast.success(`Pasted ${grid.length} row${grid.length > 1 ? "s" : ""} from Excel`);
  };
  const setCell = (i, k, v) => setRows((p) => p.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const filled = calc.items.filter((r) => r.product_name.trim() || r.specification.trim() || n(r.qty) || n(r.rate));
  const validate = () => {
    if (!filled.length) { toast.error("Add at least one item"); return false; }
    if (!gstIncluded && !n(gstRate)) { toast.error("Enter the GST rate"); return false; }
    if (!shipIncluded && !n(shipAmt)) { toast.error("Enter the shipping amount"); return false; }
    return true;
  };
  const persist = async () => {
    const { data } = await api.put(`/quote-requests/${target.id}/worksheet`, {
      items: filled.map(({ product_name, specification, qty, rate }) => ({ product_name, specification, qty, rate })),
      gst_included: gstIncluded, gst_rate: gstRate, shipping_included: shipIncluded, shipping_amount: shipAmt,
      quote_date: `${quoteDate}:00+05:30`, internal_remark: internalRemark, client_gst: clientGst, show_client_gst: showClientGst,
    });
    return data;
  };
  const save = async () => {
    if (!validate()) return;
    setBusy(true);
    try { const data = await persist(); toast.success("Worksheet saved"); onSaved?.(data); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const openGenerate = async () => {
    if (!validate()) return;
    setBusy(true);
    try { const saved = await persist(); onSaved?.(saved); setGenOpen(true); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const copyFrom = (sib) => {
    setRows(sib.items.map((i) => ({ product_name: i.product_name, specification: i.specification, qty: String(i.qty || ""), rate: String(i.rate || "") })));
    toast.success(`Copied ${sib.items.length} items from ${sib.updated_by_name || sib.assigned_to_name}'s worksheet`);
  };

  const onKey = (e, i) => {
    if (e.key === "Enter" && !e.shiftKey && e.target.tagName === "INPUT") {
      e.preventDefault();
      if (i === rows.length - 1) setRows((p) => [...p, blankRow()]);
      setTimeout(() => document.querySelector(`[data-testid="ws-cell-${i + 1}-product_name"]`)?.focus(), 0);
    }
  };
  const cell = "w-full bg-transparent px-2 py-1.5 text-[12.5px] outline-none focus:bg-indigo-50 focus:ring-2 focus:ring-inset focus:ring-indigo-500";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl w-[calc(100vw-1.5rem)] max-h-[94vh] overflow-y-auto [&>*]:min-w-0" data-testid="quote-worksheet-dialog">
        <DialogHeader>
          <DialogTitle className="heading flex items-center gap-2 pr-6"><Table2 size={16} className="text-emerald-600 shrink-0" /> Quote Worksheet — {target.client_name}</DialogTitle>
        </DialogHeader>

        <div className="max-h-[46vh] overflow-auto rounded-md border border-slate-300" data-testid="ws-grid">
          <table className="w-full min-w-[760px] border-collapse text-left">
            <thead className="sticky top-0 z-10">
              <tr className="bg-slate-900 text-white text-[10.5px] uppercase tracking-wider">
                <th className="w-12 border-r border-slate-700 px-2 py-2 text-center">S/N</th>
                <th className="w-[22%] border-r border-slate-700 px-2 py-2">Product Name</th>
                <th className="border-r border-slate-700 px-2 py-2">Specification</th>
                <th className="w-20 border-r border-slate-700 px-2 py-2 text-right">Qty</th>
                <th className="w-28 border-r border-slate-700 px-2 py-2 text-right">Rate</th>
                <th className="w-32 border-r border-slate-700 px-2 py-2 text-right">Total Cost</th>
                <th className="w-9" />
              </tr>
            </thead>
            <tbody>
              {calc.items.map((r, i) => (
                <tr key={i} className="border-b border-slate-200 odd:bg-white even:bg-slate-50/60" onKeyDown={(e) => onKey(e, i)} data-testid={`ws-row-${i}`}>
                  <td className="border-r border-slate-200 text-center text-[11px] font-semibold text-slate-500 mono">{i + 1}</td>
                  <td className="border-r border-slate-200"><input value={r.product_name} onChange={(e) => setCell(i, "product_name", e.target.value)} className={cell} onPaste={(e) => onPaste(e, i, "product_name")} data-testid={`ws-cell-${i}-product_name`} /></td>
                  <td className="border-r border-slate-200"><textarea rows={1} value={r.specification} onChange={(e) => setCell(i, "specification", e.target.value)} className={`${cell} resize-y min-h-[32px] block`} onPaste={(e) => onPaste(e, i, "specification")} data-testid={`ws-cell-${i}-specification`} /></td>
                  <td className="border-r border-slate-200"><input inputMode="decimal" value={r.qty} onChange={(e) => setCell(i, "qty", e.target.value.replace(/[^0-9.]/g, ""))} className={`${cell} text-right mono`} onPaste={(e) => onPaste(e, i, "qty")} data-testid={`ws-cell-${i}-qty`} /></td>
                  <td className="border-r border-slate-200"><input inputMode="decimal" value={r.rate} onChange={(e) => setCell(i, "rate", e.target.value.replace(/[^0-9.]/g, ""))} className={`${cell} text-right mono`} onPaste={(e) => onPaste(e, i, "rate")} data-testid={`ws-cell-${i}-rate`} /></td>
                  <td className="border-r border-slate-200 px-2 text-right text-[12.5px] font-semibold mono" data-testid={`ws-cell-${i}-total`}>{inr(r.total)}</td>
                  <td className="text-center">
                    <button type="button" onClick={() => setRows((p) => (p.length > 1 ? p.filter((_, j) => j !== i) : [blankRow()]))} className="text-slate-300 hover:text-rose-600" data-testid={`ws-row-delete-${i}`}><Trash2 size={13} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <button type="button" onClick={() => setRows((p) => [...p, blankRow()])} className="self-start inline-flex items-center gap-1 text-[12px] font-semibold text-indigo-700 hover:underline" data-testid="ws-add-row"><Plus size={13} /> Add Row <span className="font-normal text-slate-400">(or press Enter · paste rows straight from Excel)</span></button>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-3 text-[12.5px]">
            <label className="flex flex-wrap items-center justify-between gap-2">
              <span className="font-semibold text-slate-700">Quote Date &amp; Time <span className="font-normal text-slate-400">(IST)</span></span>
              <span className="flex items-center gap-2">
                <input type="datetime-local" value={quoteDate} onChange={(e) => setQuoteDate(e.target.value || istInputValue())} className="h-8 rounded-md border border-slate-300 px-2 text-[12px]" data-testid="ws-quote-date" />
              </span>
            </label>
            <div className="text-[11px] text-slate-500 -mt-2 text-right mono" data-testid="ws-quote-date-display">{fmtIST(`${quoteDate}:00+05:30`)}</div>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="font-semibold text-slate-700">Client GST No.</span>
              <span className="flex items-center gap-2">
                <input value={clientGst} onChange={(e) => setClientGst(e.target.value.toUpperCase())} maxLength={15} placeholder="e.g. 27ABCDE1234F1Z5" className="h-8 w-44 rounded-md border border-slate-300 px-2 text-[12px] mono" data-testid="ws-client-gst" />
                <label className="flex items-center gap-1 text-[11px] text-slate-600"><input type="checkbox" checked={showClientGst} onChange={(e) => setShowClientGst(e.target.checked)} data-testid="ws-client-gst-toggle" /> Show on PDF</label>
              </span>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="font-semibold text-slate-700">Is Sub Total inclusive of GST?</span>
              <YesNo value={gstIncluded} onChange={setGstIncluded} testid="ws-gst-included" />
            </div>
            {!gstIncluded && (
              <label className="flex items-center justify-between gap-2">
                <span className="text-slate-600">GST Rate (%)</span>
                <input inputMode="decimal" value={gstRate} onChange={(e) => setGstRate(e.target.value.replace(/[^0-9.]/g, ""))} className="h-8 w-28 rounded-md border border-slate-300 px-2 text-right mono" data-testid="ws-gst-rate" />
              </label>
            )}
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="font-semibold text-slate-700">Is Shipping included?</span>
              <YesNo value={shipIncluded} onChange={setShipIncluded} testid="ws-ship-included" />
            </div>
            {!shipIncluded && (
              <label className="flex items-center justify-between gap-2">
                <span className="text-slate-600">Shipping Amount (₹)</span>
                <input inputMode="decimal" value={shipAmt} onChange={(e) => setShipAmt(e.target.value.replace(/[^0-9.]/g, ""))} className="h-8 w-28 rounded-md border border-slate-300 px-2 text-right mono" data-testid="ws-ship-amount" />
              </label>
            )}
          </div>
          <div className="rounded-md border border-slate-300 bg-slate-50 text-[12.5px]" data-testid="ws-summary">
            <div className="flex justify-between px-3 py-2 border-b border-slate-200"><span className="font-semibold">Sub Total Cost <span className="font-normal text-slate-400">({filled.length} items)</span></span><span className="mono" data-testid="ws-subtotal">{inr(calc.subtotal)}</span></div>
            {!gstIncluded && <div className="flex justify-between px-3 py-2 border-b border-slate-200"><span>GST @ {n(gstRate)}%</span><span className="mono" data-testid="ws-gst-amount">{inr(calc.gst)}</span></div>}
            {!shipIncluded && <div className="flex justify-between px-3 py-2 border-b border-slate-200"><span>Shipping</span><span className="mono" data-testid="ws-shipping">{inr(calc.shipping)}</span></div>}
            <div className="flex justify-between px-3 py-2.5 bg-slate-900 text-white rounded-b-md"><span className="font-bold">Grand Total</span><span className="mono font-bold" data-testid="ws-grand-total">{inr(calc.grand)}</span></div>
          </div>
        </div>

        <div>
          <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Internal Remark <span className="normal-case font-normal text-slate-400">(never printed on the quote · visible to the team on the quote card)</span></label>
          <textarea value={internalRemark} onChange={(e) => setInternalRemark(e.target.value)} rows={2} className="mt-1 w-full rounded-md border border-amber-200 bg-amber-50/40 px-3 py-2 text-[12.5px]" data-testid="ws-internal-remark" />
        </div>

        {siblings.length > 0 && <SiblingPanel siblings={siblings} onCopy={copyFrom} />}

        <GenerateQuoteDialog open={genOpen} onOpenChange={setGenOpen} target={target} />

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="ws-close">Close</Button>
          <Button variant="outline" onClick={openGenerate} disabled={busy} className="text-rose-700 border-rose-200 hover:bg-rose-50" data-testid="ws-generate-quote"><FileDown size={14} className="mr-1" /> Generate Quote</Button>
          <Button onClick={save} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="ws-save"><Save size={14} className="mr-1" /> {busy ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SiblingPanel({ siblings, onCopy }) {
  const [openId, setOpenId] = useState(null);
  return (
    <div className="rounded-md border border-indigo-200 bg-indigo-50/40 p-3" data-testid="ws-siblings">
      <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-indigo-800"><History size={12} /> Other worksheets for this school ({siblings.length})</div>
      <div className="mt-2 space-y-1.5">
        {siblings.map((sb) => (
          <div key={sb.id} className="rounded border border-indigo-100 bg-white" data-testid={`ws-sibling-${sb.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2 px-2.5 py-1.5 text-[11.5px]">
              <button type="button" onClick={() => setOpenId(openId === sb.id ? null : sb.id)} className="text-left min-w-0" data-testid={`ws-sibling-toggle-${sb.id}`}>
                <span className="font-semibold text-slate-800">{sb.updated_by_name || sb.assigned_to_name || "—"}</span>
                <span className="text-slate-400"> · {fmtIST(sb.updated_at)} · {sb.items.length} items · {inr(sb.grand_total)} · {sb.stage}</span>
              </button>
              <Button size="sm" variant="outline" className="h-6 px-2 text-[10.5px]" onClick={() => onCopy(sb)} data-testid={`ws-sibling-copy-${sb.id}`}><Copy size={10} className="mr-1" /> Copy items</Button>
            </div>
            {sb.internal_remark && <div className="px-2.5 pb-1 text-[11px] text-amber-800">Remark: {sb.internal_remark}</div>}
            {openId === sb.id && (
              <table className="w-full border-t border-indigo-100 text-[11px]">
                <thead><tr className="bg-slate-50 text-slate-500"><th className="px-2 py-1 text-left">Product</th><th className="px-2 py-1 text-left">Specification</th><th className="px-2 py-1 text-right">Qty</th><th className="px-2 py-1 text-right">Rate</th><th className="px-2 py-1 text-right">Total</th></tr></thead>
                <tbody>{sb.items.map((i, k) => (<tr key={k} className="border-t border-slate-100"><td className="px-2 py-1">{i.product_name}</td><td className="px-2 py-1 text-slate-500">{i.specification}</td><td className="px-2 py-1 text-right mono">{i.qty}</td><td className="px-2 py-1 text-right mono">{inr(i.rate)}</td><td className="px-2 py-1 text-right mono">{inr(i.total)}</td></tr>))}</tbody>
              </table>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
